import { z } from "zod"

import {
  branchIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const boundedText = (maximum: number) => z.string().trim().min(1).max(maximum)

export const serviceOrderStateV1Schema = z.enum([
  "draft",
  "ready_to_schedule",
  "partially_scheduled",
  "scheduled",
  "in_progress",
  "on_hold",
  "completed",
  "canceled",
  "billing_review",
])

export const serviceOrderSourceV1Schema = z.enum([
  "direct",
  "estimate",
  "agreement_occurrence",
])

export const directServiceOrderCreateV1Schema = z.object({
  serviceOrderId: entityIdSchema,
  customerId: entityIdSchema,
  serviceLocationId: entityIdSchema,
  billingProfileId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  scope: boundedText(10_000),
  lineItems: z.array(z.object({
    serviceId: entityIdSchema,
    pricingSnapshotId: entityIdSchema,
  }).strict()).min(1).max(100),
  taxSnapshotId: entityIdSchema,
  paymentPolicyDecisionId: entityIdSchema,
  billingMode: boundedText(100),
  visitPlan: z.object({
    kind: z.enum(["single", "finite"]),
    occurrenceCount: z.number().int().min(1).max(100),
    expectedDurationMinutes: z.number().int().min(1).max(10_080),
    requiredSkills: z.array(boundedText(100)).max(50),
    resourceRequirements: z.array(boundedText(200)).max(50),
  }).strict(),
  invoiceable: z.boolean(),
  operationalInstructions: z.string().trim().max(5_000),
}).strict().superRefine((value, context) => {
  if (value.visitPlan.kind === "single" && value.visitPlan.occurrenceCount !== 1) {
    context.addIssue({
      code: "custom",
      path: ["visitPlan", "occurrenceCount"],
      message: "A single plan has exactly one occurrence.",
    })
  }
})

export const estimateServiceOrderConvertV1Schema = z.object({
  serviceOrderId: entityIdSchema,
  estimateId: entityIdSchema,
  revisionId: entityIdSchema,
  commercialVersionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
}).strict()

export type DirectServiceOrderCreateV1 = z.infer<
  typeof directServiceOrderCreateV1Schema
>
export type EstimateServiceOrderConvertV1 = z.infer<
  typeof estimateServiceOrderConvertV1Schema
>
